import { useEffect } from "react";
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store/react";
import { omit } from "lodash";

import {
    calculateSubtotalPrice,
    calculateSummedVatPrice,
    calculateTotalPrice,
} from "../utils/priceCalculations";

export const shoppingCartStore = createStore({
    context: {
        products: [],
        transportProduct: null,
        handlingFeeProduct: null,
        isInitialized: false,
    },
    emits: {
        productRemovedFromCart: (payload) => {},
        productUpdatedFromCart: (payload) => {},
        transportProductUpdatedFromCart: (payload) => {},
    },
    on: {
        setInitialState: (context, event) => {
            return {
                ...context,
                ...event.payload,
                isInitialized: true,
            };
        },

        addProduct: (context, event, enqueue) => {
            return {
                ...context,
                products: [...context.products, event.payload.product],
            };
        },

        removeProduct: (context, event, enqueue) => {
            enqueue.emit.productRemovedFromCart({ id: event.payload.id });

            return {
                ...context,
                products: context.products.filter(
                    (product) => product.id !== event.payload.id
                ),
            };
        },

        setProducts: (context, event) => ({
            ...context,
            products: event.payload.products,
        }),

        updateProduct: (context, event, enqueue) => {
            enqueue.emit.productUpdatedFromCart({
                id: event.payload.id,
                data: event.payload.data,
            });

            return {
                ...context,
                products: context.products.map((product) => {
                    if (product.id === event.payload.id) {
                        return {
                            ...product,
                            ...event.payload.data,
                        };
                    }

                    return product;
                }),
            };
        },

        clearCart: () => ({
            products: [],
        }),

        setTransportProduct: (context, event, enqueue) => {
            if (event.source === "cart") {
                enqueue.emit.transportProductUpdatedFromCart(event.payload);
            }

            return {
                ...context,
                transportProduct: event.payload,
            };
        },

        setHandlingFeeProduct: (context, event) => ({
            ...context,
            handlingFeeProduct: event.payload,
        }),
    },
});

export function useShoppingCartStore() {
    useEffect(() => {
        const initialState =
            JSON.parse(localStorage.getItem("shopping-cart")) ?? null;

        shoppingCartStore.send({
            type: "setInitialState",
            payload: initialState ?? {},
        });
    }, []);

    useEffect(() => {
        const subscription = shoppingCartStore.subscribe((state) => {
            localStorage.setItem(
                "shopping-cart",
                JSON.stringify(
                    omit(
                        state.context,
                        // We don't want to persist the transport and handling fee products
                        // because they will be fetched dynamically from the server
                        ["transportProduct", "handlingFeeProduct"]
                    )
                )
            );
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    return shoppingCartStore;
}

export function useSubtotalPrice() {
    return useSelector(shoppingCartStore, (state) =>
        calculateSubtotalPrice(
            [
                ...state.context.products,
                state.context.transportProduct,
                state.context.handlingFeeProduct,
            ]
                .filter(Boolean)
                .map((product) => ({
                    amount: Number(product.amount ?? 1),
                    netPrice: product.price?.price,
                    grossPrice: product.price?.priceGross,
                    tax: product.price?.vat,
                }))
        )
    );
}

export function useVatPrice() {
    return useSelector(shoppingCartStore, (state) =>
        calculateSummedVatPrice(
            [
                ...state.context.products,
                state.context.transportProduct,
                state.context.handlingFeeProduct,
            ]
                .filter(Boolean)
                .map((product) => ({
                    amount: Number(product.amount ?? 1),
                    netPrice: product.price?.price,
                    grossPrice: product.price?.priceGross,
                    tax: product.price?.vat,
                }))
        )
    );
}

export function useTotalPrice() {
    return useSelector(shoppingCartStore, (state) =>
        calculateTotalPrice(
            [
                ...state.context.products,
                state.context.transportProduct,
                state.context.handlingFeeProduct,
            ]
                .filter(Boolean)
                .map((product) => ({
                    amount: Number(product.amount ?? 1),
                    netPrice: product.price?.price,
                    grossPrice: product.price?.priceGross,
                    tax: product.price?.vat,
                }))
        )
    );
}

export function useShoppingCartProducts() {
    return useSelector(shoppingCartStore, (state) => state.context.products);
}

export function useShoppingCartIsInitialized() {
    return useSelector(
        shoppingCartStore,
        (state) => state.context.isInitialized
    );
}
